/**
 * @name InfoNumberCard
 * @description Componente de tipo tarjeta numérica con icono y color para dashboard's
 * @author Ricardo Guerra
 */

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import CountUp from 'react-countup';

import { HelpTooltip } from '@tycommon/components';
import { MUI, Unicons, Theme, Typography } from '@tycommon/styles';

const InfoNumberCard = (props) => {
  const {
    color,
    secondColor,
    backgroundColor,
    backgroundImage,
    title,
    info,
    help,
    icon,
    number,
    delay,
    decimals,
    concatString,
    iconComponent,
    canSelect,
    isSelected
  } = props;

  return (
    <Styled.Card
      color={color}
      secondColor={secondColor}
      backgroundColor={backgroundColor}
      canSelect={canSelect}
      isSelected={isSelected}
    >
      {color && <Styled.ColorBar color={color} />}
      <Styled.Container hasColor={Boolean(color)}>
        {/* -- CARD HEADER -- */}
        <Styled.Header>
          <Styled.HeaderTitle>{title}</Styled.HeaderTitle>
          {/* Info Tooltip */}
          {info && (
            <MUI.Tooltip title={<MUI.Box padding="4px" style={{ backgroundColor: Theme.colors.white }}>{info}</MUI.Box>}>
              <Unicons.CircleInfo size="20" color={Theme.colors.greyDark} style={{ marginLeft: '8px' }} />
            </MUI.Tooltip>
          )}
          {/* Help Tooltip */}
          {help && (
            <Styled.HeaderHelp>
              <HelpTooltip message={<MUI.Box padding="4px" style={{ backgroundColor: Theme.colors.greyDark }}>{help}</MUI.Box>} />
            </Styled.HeaderHelp>
          )}
          {/* Header Icono */}
          {icon && (
            <Styled.HeaderIcon>
              {icon}
            </Styled.HeaderIcon>
          )}
        </Styled.Header>
        {/* -- CARD CONTENT -- */}
        <Styled.Content>
          {/* Número */}
          <Styled.Data>
            <CountUp end={number} decimals={decimals} delay={delay ? delay : 1} />
            {concatString && concatString}
          </Styled.Data>
          {/* Icono - Component */}
          {iconComponent && (
            <Styled.IconComponent>{iconComponent}</Styled.IconComponent>
          )}
          {/* Icono - Background */}
          {backgroundImage && (
            <Styled.ImageBackground>{backgroundImage}</Styled.ImageBackground>
          )}
        </Styled.Content>
      </Styled.Container>
    </Styled.Card>
  );
};

InfoNumberCard.propTypes = {
  // Color primario de la tarjeta (Theme Colors).
  color: PropTypes.string,
  // Color secundario de la tarjeta (Theme Colors).
  secondColor: PropTypes.string,
  // Color del background de la tarjeta (Theme Colors).
  backgroundColor: PropTypes.string,
  // Background component to render.
  backgroundImage: PropTypes.node,
  // Titulo de la tarjeta.
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  // Mensaje de info dentro del Tooltip.
  info: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  // Mensaje de ayuda dentro del HelpTooltip.
  help: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  // Icon component to render.
  icon: PropTypes.node,
  // Numero de la tarjeta.
  number: PropTypes.any.isRequired,
  // Segundos de delay para la animación CountUp. [Default: 2 seg]
  delay: PropTypes.number,
  // Decimales del número CountUp.
  decimals: PropTypes.number,
  // Cadena que se concatena al numero puede ser ('%' | '/#' o un nodo React).
  concatString: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  // Child component to render.
  iconComponent: PropTypes.node,
  // Background component to render.
  background: PropTypes.node,
  // Boolean para permitir seleccionar la tarjeta.
  canSelect: PropTypes.bool,
  // Boolean que indica si la tarjeta esta seleccionada.
  isSelected: PropTypes.bool
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const Styled = {};

Styled.Card = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  border-radius: 6px;
  box-shadow: ${(props) => props.theme.shadows.depth2};
  box-shadow: ${(props) => (props.canSelect && props.isSelected && !props.secondColor) && props.theme.shadows.depth4};
  box-shadow: ${(props) => (props.canSelect && props.isSelected && props.secondColor) && `0px 5px 5px -3px ${props.theme.colors[props.secondColor]}, 0px 8px 10px 1px ${props.theme.colors[props.secondColor]}, 0px 3px 14px 2px ${props.theme.colors[props.secondColor]}`};
  background-color: ${(props) => props.theme.colors[props.backgroundColor]};
  &:hover {
    box-shadow: ${(props) => (props.canSelect && !props.isSelected) && props.theme.shadows.depth4};
    cursor: ${(props) => props.canSelect && `pointer`};
  }
`;

Styled.ColorBar = styled.div`
  width: 8px;
  min-width: 8px;
  background-color: ${(props) => props.theme.colors[props.color]};
`;

Styled.Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 8px;
  //overflow-y: auto;
  overflow: hidden;
  scrollbar-width: thin !important;
  border-radius: ${(props) => props.hasColor ? '0 6px 6px 0' : '6px'};
`;

Styled.Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px;
  z-index: 2;
`;

Styled.HeaderTitle = styled(Typography.Text)`
  // flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.3;
`;

Styled.HeaderHelp = styled.div`
  display: flex;
  flex: 1;
  justify-content: flex-end;
  padding-left: 8px;
  height: 20px;
  align-self: flex-start;

  .MuiSvgIcon-root {
    font-size: 20px;
  }
`;

Styled.HeaderIcon = styled.div`
  display: flex;
  flex: 1;
  justify-content: flex-end;
  padding-left: 8px;
  height: 20px;
  align-self: flex-start;

  .MuiSvgIcon-root {
    font-size: 20px;
  }
`;

Styled.Content = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  padding: 0 8px 8px;
  z-index: 1;
`;

Styled.Data = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;

  & > span {
    font-family: 'Bryant Pro Medium';
    font-variant: tabular-nums;
    font-size: 36px;
    line-height: 28px;
  }

  & > p {
    font-family: 'Bryant Pro Medium';
    font-variant: tabular-nums;
    font-size: 32px;
    line-height: 22px;
    color: ${(props) => props.theme.colors.greyDarkAlpha};
  }
`;

Styled.IconComponent = styled.div`
  position: absolute;
  right: 10px;
  bottom: 10px;
  z-index: -1;
`;

Styled.ImageBackground = styled.div`
  position: absolute;
  right: -32px;
  bottom: -32px;
  z-index: -1;
`;

export default InfoNumberCard;